import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { API_BASE, page, cardScroll, inp, btn, ConfirmDialog } from './AdminUsers';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// Group member management (2026-08-05), reached by clicking a row on
// AdminGroups.jsx. Adding a member uses /admin/users/filter (2026-10-01) —
// free-text search plus Designation/Role/Account-Type dropdowns, live as
// you type/select — rather than listing every user here: groups can have
// far more eligible non-members than members, so nothing is shown until
// the admin gives at least one real criterion. /admin/users/search (the
// plain AdminUsers.jsx directory search box) only reaches Authentik's own
// `?search=`, which doesn't match the custom attributes fields (designation,
// role, etc.) this picker needs to filter by — see /filter's own comment
// in adminUsers.js for why that needed a different endpoint, not a tweak
// to the existing one.

export default function AdminGroupDetail() {
  const navigate = useNavigate();
  const { pk } = useParams();

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [designation, setDesignation] = useState('');
  const [role, setRole] = useState('');
  const [accountType, setAccountType] = useState('');
  const [facets, setFacets] = useState({ designations: [], roles: [], accountTypes: [] });
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingPk, setAddingPk] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState(null);
  const [removingPk, setRemovingPk] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(false);

  async function fetchGroup() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/groups/${pk}`, {
        credentials: 'include',
      });
      if (res.status === 403) {
        setError('You are not authorized to access this page.');
        return;
      }
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      if (res.status === 404) {
        setError('This group no longer exists.');
        setGroup(null);
        return;
      }
      const data = await res.json();
      setGroup(data);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setLoading(false);
    }
  }

  // Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
  // header comment) — no more synchronous "is there a token" pre-check;
  // fetchGroup()'s own 401/403 handling already covers both cases.
  useEffect(() => {
    fetchGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pk]);

  // Populates the filter dropdowns with the real, currently-in-use values
  // (not a hardcoded guess) before any search/filter has actually run —
  // /filter computes facets from its full scan regardless of the filters
  // passed in, so this one no-criteria call is enough; its own `results`
  // are deliberately discarded here (see the live-filter effect below for
  // why the candidate list itself stays empty until the admin gives some
  // actual criterion).
  useEffect(() => {
    fetch(`${API_BASE}/admin/users/filter`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => { if (data.facets) setFacets(data.facets); })
      .catch(() => {}); // non-fatal — dropdowns just stay empty, free-text search still works
  }, []);

  // Live, combined search+filter — same debounced/self-cancelling pattern
  // as AdminUsers.jsx's directory search. Deliberately does NOT fire with
  // every field empty (unlike AdminUsers.jsx's directory, which always
  // shows something) — this file's own original design note still holds:
  // groups can have far more eligible non-members than members, so
  // showing nothing until the admin gives at least one real criterion
  // (text OR a dropdown) avoids dumping the entire user base here by
  // default.
  useEffect(() => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery && !designation && !role && !accountType) {
      setSearchResults([]);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (trimmedQuery) params.set('q', trimmedQuery);
        if (designation) params.set('designation', designation);
        if (role) params.set('role', role);
        if (accountType) params.set('accountType', accountType);
        const res = await fetch(`${API_BASE}/admin/users/filter?${params.toString()}`, {
          credentials: 'include',
          signal: controller.signal,
        });
        const data = await res.json();
        setSearchResults(data.results || []);
        if (data.facets) setFacets(data.facets);
      } catch (e) {
        if (e.name !== 'AbortError') {
          setError('Could not reach the identity service.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setSearching(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, designation, role, accountType]);

  async function handleAddMember(user) {
    setAddingPk(user.pk);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/groups/${pk}/members`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userPk: user.pk }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not add user to group.');
        return;
      }
      await fetchGroup();
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setAddingPk(null);
    }
  }

  function handleRemoveMember(member) {
    setConfirmDialog({
      title: 'Remove from Group',
      message: `Remove ${member.username} from ${group.name}?`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: () => doRemoveMember(member),
    });
  }

  async function doRemoveMember(member) {
    setConfirmDialog(null);
    setRemovingPk(member.pk);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/groups/${pk}/members/${member.pk}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not remove user from group.');
        return;
      }
      await fetchGroup();
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setRemovingPk(null);
    }
  }

  function handleDeleteGroup() {
    setConfirmDialog({
      title: 'Delete Group',
      message: `Permanently delete "${group.name}"? This removes all ${group.members.length} member${group.members.length === 1 ? '' : 's'} from it and cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: doDeleteGroup,
    });
  }

  async function doDeleteGroup() {
    setConfirmDialog(null);
    setDeletingGroup(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/groups/${pk}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not delete group.');
        return;
      }
      navigate('/admin/groups');
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setDeletingGroup(false);
    }
  }

  const memberPks = new Set((group?.members || []).map((m) => String(m.pk)));

  return (
    <>
      <TopBanner />
      <div style={page}>
      <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
      <AdminSidebar active="users" />
      <div style={cardScroll}>
        <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: '10px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>
              {loading ? 'Loading…' : group ? `Group: ${group.name}` : 'Group not found'}
            </h1>
            {group && (
              <button
                type="button"
                onClick={handleDeleteGroup}
                disabled={deletingGroup}
                style={{
                  background: '#B3261E', border: 'none', color: '#fff',
                  borderRadius: '8px', fontSize: '13px', fontWeight: '600', padding: '8px 16px',
                  cursor: deletingGroup ? 'not-allowed' : 'pointer', opacity: deletingGroup ? 0.6 : 1,
                }}
              >
                {deletingGroup ? 'Deleting…' : 'Delete Group'}
              </button>
            )}
          </div>
          <Link
            to="/admin/groups"
            style={{ display: 'inline-block', background: '#5F5E5A', border: '1px solid #5F5E5A', color: '#fff', textDecoration: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}
          >
            ← Back to Groups
          </Link>
        </div>

        {error && (
          <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {group && (
          <>
            <div style={{ marginBottom: '28px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 10px' }}>
                Members ({group.members.length})
              </h2>
              <div style={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'hidden' }}>
                {group.members.map((m) => (
                  <div
                    key={m.pk}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 16px', borderBottom: '1px solid #f0f0f0', background: '#fff',
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>{m.username}</span>
                      <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                        {m.email || 'no email'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(m)}
                      disabled={removingPk === m.pk}
                      style={{ background: 'none', border: '1px solid #B3261E', color: '#B3261E', borderRadius: '6px', fontSize: '12px', padding: '4px 10px', cursor: removingPk === m.pk ? 'not-allowed' : 'pointer', opacity: removingPk === m.pk ? 0.6 : 1 }}
                    >
                      {removingPk === m.pk ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                ))}
                {group.members.length === 0 && (
                  <div style={{ padding: '14px 16px', fontSize: '13px', color: '#666' }}>No members yet.</div>
                )}
              </div>
            </div>

            <div>
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 10px' }}>
                Add a member
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
                <input
                  style={{ ...inp, flex: '1 1 260px' }}
                  type="text"
                  placeholder="Search by username, email, or phone"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <select
                  style={{ ...inp, width: 'auto' }}
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                >
                  <option value="">All designations</option>
                  {facets.designations.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <select
                  style={{ ...inp, width: 'auto' }}
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="">All roles</option>
                  {facets.roles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <select
                  style={{ ...inp, width: 'auto' }}
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                >
                  <option value="">All account types</option>
                  {facets.accountTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {(designation || role || accountType || query.trim()) && (
                  <button
                    type="button"
                    onClick={() => { setQuery(''); setDesignation(''); setRole(''); setAccountType(''); }}
                    style={{ ...btn, width: 'auto', marginTop: 0, background: 'none', border: '1px solid #ccc', color: '#5F5E5A' }}
                  >
                    Clear filters
                  </button>
                )}
                {searching && <span style={{ fontSize: '12px', color: '#666' }}>Searching…</span>}
              </div>

              {searchResults.length > 0 && (
                <div style={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'hidden' }}>
                  {searchResults.map((u) => {
                    const already = memberPks.has(String(u.pk));
                    return (
                      <div
                        key={u.pk}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '10px 16px', borderBottom: '1px solid #f0f0f0', background: '#fff',
                        }}
                      >
                        <div>
                          <span style={{ fontWeight: '600', fontSize: '14px' }}>{u.username}</span>
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                            {u.email || 'no email'} · {u.phone || 'no phone'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddMember(u)}
                          disabled={already || addingPk === u.pk}
                          style={{ ...btn, width: 'auto', marginTop: 0, padding: '4px 12px', fontSize: '12px', opacity: (already || addingPk === u.pk) ? 0.5 : 1, cursor: (already || addingPk === u.pk) ? 'not-allowed' : 'pointer' }}
                        >
                          {already ? 'Already a member' : addingPk === u.pk ? 'Adding…' : 'Add'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      </div>

      <ConfirmDialog dialog={confirmDialog} onCancel={() => setConfirmDialog(null)} />
      </div>
    </>
  );
}
